import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BugReport,
  ControlPointDuplicate,
  MoreVert,
  PlaylistAdd,
  Settings,
  Store,
  Unarchive,
} from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { KeyedMutator } from 'swr/_internal';
import { SelectChangeEvent } from '@mui/material/Select';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AccountData, AccountService, OrderPageData, OrderPageTypes } from '../api';
import CopyToClipboardButton from '../calendar/dialogs/CopyToClipboardButton';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';
import { useLanguage } from '../common/GeneralUtils';
import { useAuth } from '../session/InternalAuthProvider';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { getPassOrderPages } from '../utils/AccountUtils';

type OrderPageCardProps = {
  orderPage: OrderPageData;
  isPartOfPass: boolean;
  passNames: string[];
  isArchived: boolean;
  updateLocalAccount: KeyedMutator<AccountData>;
};

// Palette paths, resolved by `sx`.
const TYPE_COLORS: Record<OrderPageTypes, string> = {
  [OrderPageTypes.PRIVATE]: 'text.primary',
  [OrderPageTypes.COURSE]: 'secondary.dark',
  [OrderPageTypes.EVENT]: 'primary.main',
};

const OrderPageCard = ({ orderPage, isPartOfPass, passNames, isArchived, updateLocalAccount }: OrderPageCardProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState?.account as unknown as AccountData;
  const language = useLanguage();
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const navigate = useNavigate();

  const passesEnabled = useFeatureFlagEnabled('feature-passes');
  const passOrderPages = getPassOrderPages(account, true);
  const eligiblePasses = passOrderPages.filter((p) => !p.redeemable_on?.some((r) => r.order_page_id === orderPage.id));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneType, setCloneType] = useState<OrderPageTypes>(orderPage.order_page_type);
  const [cloneSessions, setCloneSessions] = useState<number | ''>(orderPage.course_sessions_number ?? '');
  const [addToPassOpen, setAddToPassOpen] = useState(false);
  const [selectedPassIds, setSelectedPassIds] = useState<string[]>([]);

  const bookingPageLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/experience/${account.id}/`;
  const accentColor = TYPE_COLORS[orderPage.order_page_type] ?? 'primary.main';

  const typeLabel =
    orderPage.order_page_type === OrderPageTypes.PRIVATE
      ? t('order_pages.private')
      : orderPage.order_page_type === OrderPageTypes.COURSE
      ? t('order_pages.course')
      : t('order_pages.join');

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleClone = async (confirmed: boolean) => {
    setCloneDialogOpen(false);
    if (!confirmed) return;
    const existingIds = new Set(account.experiences[0].order_pages.map((p) => p.id));
    const updatedAccount = await safeExecute({
      apiCall: () =>
        AccountService.cloneOrderPage({
          order_page_id: orderPage.id,
          order_page_type: cloneType,
          ...(cloneType === OrderPageTypes.COURSE && cloneSessions !== ''
            ? { course_sessions_number: cloneSessions }
            : {}),
        }),
      successMessage: t('order_pages.clone_success'),
    });
    await updateLocalAccount(updatedAccount);
    const newPage = updatedAccount?.experiences[0].order_pages.find((p: OrderPageData) => !existingIds.has(p.id));
    if (newPage) navigate(`/dashboard/settings/${newPage.id}`);
  };

  const handleUnarchive = async (confirmed: boolean) => {
    setUnarchiveDialogOpen(false);
    if (!confirmed) return;
    const updatedAccount = await safeExecute({
      apiCall: () => AccountService.restoreOrderPage(orderPage.id),
      successMessage: t('order_pages.unarchive_success'),
    });
    await updateLocalAccount(updatedAccount);
  };

  const handleAddToPass = async (confirmed: boolean) => {
    setAddToPassOpen(false);
    setSelectedPassIds([]);
    if (!confirmed || selectedPassIds.length === 0) return;
    let updatedAccount;
    for (const passId of selectedPassIds) {
      const pass = passOrderPages.find((p) => p.id === passId)!;
      const existing = pass.redeemable_on ?? [];
      updatedAccount = await safeExecute({
        apiCall: () =>
          AccountService.updatePassOrderPage({
            id: passId,
            redeemable_on: [
              ...existing.map((r) => ({ order_page_id: r.order_page_id, limit: r.limit })),
              { order_page_id: orderPage.id },
            ],
            num_punches: pass.num_punches,
          }),
        successMessage: t('order_pages.add_to_pass'),
      });
    }
    if (updatedAccount) await updateLocalAccount(updatedAccount);
  };

  return (
    <>
      <Card
        sx={{
          width: { xs: '100%', sm: 260 },
          border: '1px solid',
          borderColor: 'divider',
          borderInlineStart: '4px solid',
          borderInlineStartColor: accentColor,
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/dashboard/settings/${orderPage.id}`)}>
        <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
          <Grid container justifyContent="space-between" alignItems="center" mb={1}>
            <Grid item>
              <Grid container gap={0.5} alignItems="center">
                <Chip
                  size="small"
                  label={typeLabel}
                  sx={{ backgroundColor: accentColor, color: 'background.paper', fontWeight: 700 }}
                />
                {isPartOfPass && (
                  <Tooltip title={passNames.join(', ')} placement="top" disableHoverListener={passNames.length === 0}>
                    <Chip size="small" label={t('order_pages.in_passes')} variant="outlined" />
                  </Tooltip>
                )}
              </Grid>
            </Grid>
            {!isArchived && (
              <Grid item>
                <IconButton size="small" onClick={handleMenuOpen}>
                  <MoreVert fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>

          <Grid container alignItems="center" gap={1} mb={1.5} flexWrap="nowrap">
            <WorkshopCircle account={account} orderPageId={orderPage.id} language={language} />
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                flex: 1,
                minWidth: 0,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {orderPage.label[language]}
            </Typography>
          </Grid>

          {orderPage.order_page_type === OrderPageTypes.COURSE && orderPage.course_sessions_number != null && (
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {t('order_pages.sessions_number')}: {orderPage.course_sessions_number}
            </Typography>
          )}

          {!isArchived && (
            <Grid container alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
              <CopyToClipboardButton
                orderPageId={orderPage.id}
                text={bookingPageLink + orderPage.id}
                color={theme.palette.text.secondary}
                width={'20px'}
              />
              <Typography
                variant="subtitle2"
                component="a"
                href={bookingPageLink + orderPage.id}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {t('order_pages.order_page')}
              </Typography>
            </Grid>
          )}

          {isArchived && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                setUnarchiveDialogOpen(true);
              }}
              startIcon={<Unarchive fontSize="small" />}
              sx={{ mt: 0.5 }}>
              {t('order_pages.unarchive')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose} component={Link} to={`/dashboard/settings/${orderPage.id}`}>
          <Settings fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.settings')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(bookingPageLink + orderPage.id, '_blank');
          }}>
          <Store fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.order_page')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(`${bookingPageLink}${orderPage.id}?nomindate=true`, '_blank');
          }}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Grid container alignItems="center" gap={1} flexWrap="nowrap">
            <Store fontSize="small" />
            {t('order_pages.order_page_today')}
          </Grid>
          <span onClick={(e) => e.stopPropagation()}>
            <CopyToClipboardButton
              orderPageId={orderPage.id}
              text={`${bookingPageLink}${orderPage.id}?nomindate=true`}
              color={theme.palette.text.secondary}
              width="16px"
            />
          </span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            window.open(`${bookingPageLink}${orderPage.id}?troubleshoot=true`, '_blank');
          }}>
          <BugReport fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.troubleshoot')}
        </MenuItem>
        {passesEnabled && passOrderPages.length > 0 && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setSelectedPassIds([]);
              setAddToPassOpen(true);
            }}>
            <PlaylistAdd fontSize="small" sx={{ marginInlineEnd: 1 }} />
            {t('order_pages.add_to_pass')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setCloneType(orderPage.order_page_type);
            setCloneSessions(orderPage.course_sessions_number ?? '');
            setCloneDialogOpen(true);
          }}>
          <ControlPointDuplicate fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('order_pages.clone')}
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        isOpen={unarchiveDialogOpen}
        onClose={handleUnarchive}
        title={t('order_pages.unarchive_confirmation_title')}
        content={t('order_pages.unarchive_confirmation_content')}
        ctaText={t('order_pages.unarchive')}
        confirmButtonColor="primary"
      />

      <ConfirmationDialog
        isOpen={cloneDialogOpen}
        onClose={handleClone}
        title={t('order_pages.clone')}
        content={
          <Grid container direction="column" gap={2} sx={{ mt: 1 }}>
            <FormControl size="small" sx={{ maxWidth: 250 }}>
              <InputLabel>{t('order_pages.type')}</InputLabel>
              <Select
                value={cloneType}
                label={t('order_pages.type')}
                onChange={(e: SelectChangeEvent) => setCloneType(e.target.value as OrderPageTypes)}>
                <MenuItem value={OrderPageTypes.EVENT}>{t('order_pages.join')}</MenuItem>
                <MenuItem value={OrderPageTypes.PRIVATE}>{t('order_pages.private')}</MenuItem>
                <MenuItem value={OrderPageTypes.COURSE}>{t('order_pages.course')}</MenuItem>
              </Select>
            </FormControl>
            {cloneType === OrderPageTypes.COURSE && (
              <TextField
                size="small"
                type="number"
                label={t('order_pages.sessions_number')}
                value={cloneSessions}
                onChange={(e) => setCloneSessions(e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 1 }}
                sx={{ maxWidth: 250 }}
              />
            )}
          </Grid>
        }
        ctaText={t('order_pages.clone')}
        confirmButtonColor="primary"
      />
      <ConfirmationDialog
        isOpen={addToPassOpen}
        onClose={handleAddToPass}
        title={t('order_pages.add_to_pass_title')}
        ctaText={t('order_pages.add_to_pass')}
        confirmButtonColor="primary"
        content={
          eligiblePasses.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('order_pages.add_to_pass_already_in_all')}
            </Typography>
          ) : (
            <FormGroup>
              {eligiblePasses.map((pass) => (
                <FormControlLabel
                  key={pass.id}
                  control={
                    <Checkbox
                      checked={selectedPassIds.includes(pass.id)}
                      onChange={(e) =>
                        setSelectedPassIds((prev) =>
                          e.target.checked ? [...prev, pass.id] : prev.filter((id) => id !== pass.id),
                        )
                      }
                    />
                  }
                  label={pass.label?.[language] ?? pass.id}
                />
              ))}
            </FormGroup>
          )
        }
      />
    </>
  );
};

export default OrderPageCard;
